import { useCallback, useEffect, useRef, useState } from 'react';
import { useChatStatus } from './useChatStatus';
import { usePatientChatOptional } from '@odo/shared-ui';
import { useUser } from '../providers/UserProvider';
import { useToast } from '../providers/ToastProvider';
import {
    markChatAsSeen,
    markChatAsUnread,
    setChatTeam,
    assignChatUser,
    unassignChatUser,
    type ChatType
} from '../services/chat';

export interface PatientConversationActions {
    isUnread: boolean;
    currentTeam: string | null;
    currentTeamRoleId: string | null;
    currentAssignee: string | null;
    reviewedBy: string | null;
    currentUserEmail: string;
    isMarkingRead: boolean;
    isMarkingUnread: boolean;
    handleMarkAsDone: () => Promise<void>;
    handleMarkAsUnread: () => Promise<void>;
    handleTeamChange: (team: string) => Promise<void>;
    handleAssign: (email: string) => Promise<void>;
    handleUnassign: () => Promise<void>;
}

// Read state, team and assignment come from the thread's own chat-status entry,
// so external and internal never share a cache key or overwrite each other's row.
export function usePatientConversationActions(
    patientId: string,
    chatType: ChatType = 'external',
    onReadStateChange?: () => Promise<void>
): PatientConversationActions {
    const status = useChatStatus(patientId, chatType);
    const chatCtx = usePatientChatOptional();
    const refreshActivities =
        chatCtx?.refreshActivities ??
        (async () => {
            /* noop */
        });
    const { user } = useUser();
    const { showToast } = useToast();

    const [isMarkingRead, setIsMarkingRead] = useState(false);
    const [isMarkingUnread, setIsMarkingUnread] = useState(false);
    const [optimisticTeam, setOptimisticTeam] = useState<string | null>(null);
    const [optimisticAssignee, setOptimisticAssignee] = useState<string | null | undefined>(
        undefined
    );

    const refreshStatus = status.refresh;

    // Sending a message bumps the thread's chat_status row server-side (read
    // state, last message), but that lives in a separate react-query cache
    // from the chat feed — so the team/read strip stayed stale until the next
    // poll. The chat provider swaps its feed object after every send/edit/
    // delete (and when its poll detects an update); piggyback on that swap to
    // refetch the strip's status. Skips the first feed load so mounting a tab
    // doesn't trigger a redundant fetch, and no-ops outside a chat provider.
    const feed = chatCtx?.feed ?? null;
    const lastFeedRef = useRef<typeof feed | undefined>(undefined);
    useEffect(() => {
        const prev = lastFeedRef.current;
        lastFeedRef.current = feed;
        if (prev === undefined || prev === null || feed === null || feed === prev) return;
        void refreshStatus();
    }, [feed, refreshStatus]);

    const serverAssignedTo = status.assignedTo;
    const currentTeam = optimisticTeam ?? status.team ?? 'Admin';
    const currentAssignee =
        optimisticAssignee !== undefined ? optimisticAssignee : serverAssignedTo;

    const handleMarkAsDone = useCallback(async () => {
        setIsMarkingRead(true);
        try {
            await markChatAsSeen(patientId, chatType);
            await Promise.all([refreshStatus(), refreshActivities(), onReadStateChange?.()]);
        } catch {
            showToast('Failed to mark as read', 'error');
        } finally {
            setIsMarkingRead(false);
        }
    }, [patientId, chatType, refreshStatus, refreshActivities, onReadStateChange, showToast]);

    const handleMarkAsUnread = useCallback(async () => {
        setIsMarkingUnread(true);
        try {
            await markChatAsUnread(patientId, chatType);
            await Promise.all([refreshStatus(), refreshActivities(), onReadStateChange?.()]);
        } catch {
            showToast('Failed to mark as unread', 'error');
        } finally {
            setIsMarkingUnread(false);
        }
    }, [patientId, chatType, refreshStatus, refreshActivities, onReadStateChange, showToast]);

    const handleTeamChange = useCallback(
        async (team: string) => {
            setOptimisticTeam(team);
            setOptimisticAssignee(null);
            try {
                await setChatTeam(patientId, team, chatType);
                await Promise.all([refreshStatus(), refreshActivities()]);
            } catch {
                showToast('Failed to update team', 'error');
                setOptimisticTeam(null);
                setOptimisticAssignee(undefined);
            }
        },
        [patientId, chatType, refreshStatus, refreshActivities, showToast]
    );

    const handleAssign = useCallback(
        async (email: string) => {
            setOptimisticAssignee(email);
            try {
                await assignChatUser(patientId, email, serverAssignedTo, chatType);
                await Promise.all([refreshStatus(), refreshActivities()]);
            } catch (err: unknown) {
                const isConflict =
                    (err as { response?: { status?: number } })?.response?.status === 409;
                showToast(
                    isConflict
                        ? 'Someone else just changed this assignment — refreshing'
                        : 'Failed to assign user',
                    'error'
                );
                setOptimisticAssignee(undefined);
                if (isConflict) await refreshStatus();
            }
        },
        [patientId, chatType, serverAssignedTo, refreshStatus, refreshActivities, showToast]
    );

    const handleUnassign = useCallback(async () => {
        setOptimisticAssignee(null);
        try {
            await unassignChatUser(patientId, serverAssignedTo, chatType);
            await Promise.all([refreshStatus(), refreshActivities()]);
        } catch (err: unknown) {
            const isConflict =
                (err as { response?: { status?: number } })?.response?.status === 409;
            showToast(
                isConflict
                    ? 'Someone else just changed this assignment — refreshing'
                    : 'Failed to unassign user',
                'error'
            );
            setOptimisticAssignee(undefined);
            if (isConflict) await refreshStatus();
        }
    }, [patientId, chatType, serverAssignedTo, refreshStatus, refreshActivities, showToast]);

    return {
        isUnread: status.isUnread,
        currentTeam,
        currentTeamRoleId: status.teamRoleId,
        currentAssignee,
        reviewedBy: status.reviewedBy,
        currentUserEmail: user?.email ?? '',
        isMarkingRead,
        isMarkingUnread,
        handleMarkAsDone,
        handleMarkAsUnread,
        handleTeamChange,
        handleAssign,
        handleUnassign
    };
}
